import React, {useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useStores} from '@src/components/store';
import {SystemConfiguration} from '@src/utils/system-configuration';
import {CategoryWithStatus} from '@src/dtos/category-with-status.dto';
import {Toggle} from '@src/components/shared/toggle/toggle';
import {Filters} from '@src/components/shared/filters/filters';
import {NoResult} from '@src/components/shared/no-result/no-result';
import Loader from '@src/components/shared/loader/loader';
import {ServiceItemTooltip} from '@src/components/features/services/service-item-tooltip/service-item-tooltip';
import {InactiveDnsProtectionAlert} from '@src/components/bronze/inactive-dns-protection-alert';
import {ApplyBronzeButton} from '@src/components/bronze/apply-bronze-button';
import {BronzeTabName} from '@src/dtos/analytics.dto';
import {BronzeCategoriesMoreMenu} from './bronze-categories-more-menu';
import {PresetBronzePanel} from './presets/presets-bronze';
import CustomButton from '@src/components/shared/custom-button/custom-button';
import CategoriesIcon from '@svg/categories.svg?react';
import {bronzeCategoryFiltersConfig} from './bronze-categories-filters';

import './bronze-categories.scss';

export const BronzeCategories: React.FC = observer(() => {
    const {fwVersionBronzeStore, customerStore} = useStores();
    const {abfBronzeCategoryDtoList} = SystemConfiguration.getInstance();

    const [categoryListFiltered, setCategoryListFiltered] = useState<CategoryWithStatus[] | undefined>(undefined);
    const [isPresetOpen, setIsPresetOpen] = useState(false);

    /** Build the full list enriched with current allow/block state from the store. */
    const categoryListOriginal: CategoryWithStatus[] = useMemo(() => {
        const storeCategories = fwVersionBronzeStore.bronzeCategories;
        return abfBronzeCategoryDtoList.map(category => {
            const storeEntry = storeCategories.find(c => c.name === category.categoryName);
            return {
                ...category,
                isAllow: storeEntry ? storeEntry.action !== 'block' : !category.blockedByDefault,
            };
        });
    }, [fwVersionBronzeStore.bronzeCategories, abfBronzeCategoryDtoList]);

    const onChange = (categoryName: string, isAllow: boolean) => {
        fwVersionBronzeStore.updateBronzeCategory(categoryName, isAllow);
    };

    const renderCategoryList = () => {
        if (!categoryListFiltered) return null;
        if (categoryListFiltered.length === 0) return <NoResult/>;

        return (
            <div className="bronze-category-toggle-list">
                {categoryListFiltered.map(category => (
                    <div className="card" key={category.categoryName}>
                        <div className="toggle-name">{category.categoryTitle}</div>
                        <ServiceItemTooltip
                            title={category.categoryTitle}
                            descriptionList={[category.categoryDescription]}
                        />
                        <Toggle
                            key={category.categoryName}
                            disableReason={fwVersionBronzeStore.bronzeDomainsDisableReason}
                            value={category.isAllow}
                            onChange={(val) => onChange(category.categoryName, val)}
                            withIcon={true}
                        />
                    </div>
                ))}
            </div>
        );
    };

    const isLoading = fwVersionBronzeStore.isLoading || !fwVersionBronzeStore.selectedFwVersion;

    return (
        <div className="bronze-categories" data-testid="bronze-categories">
            <InactiveDnsProtectionAlert/>
            <div className="filter-wrapper">
                <Filters
                    filterConfig={bronzeCategoryFiltersConfig}
                    setData={setCategoryListFiltered}
                    originalData={categoryListOriginal}
                />
                <div className="right-actions">
                    <CustomButton
                        className={`filter-btn preset-bronze-btn${isPresetOpen ? ' active' : ''}`}
                        label="Presets"
                        prefixIcon={<CategoriesIcon/>}
                        onClick={() => setIsPresetOpen(o => !o)}
                        data-testid="preset-bronze-btn"
                    />
                    <ApplyBronzeButton standardApplyType={BronzeTabName.CATEGORIES}/>
                    {customerStore.isAvailable && !fwVersionBronzeStore.isPendingVersion
                        && <BronzeCategoriesMoreMenu/>}
                </div>
            </div>
            {!isLoading && isPresetOpen && <PresetBronzePanel/>}
            {isLoading && <Loader type="full-screen"/>}
            {!isLoading && renderCategoryList()}
        </div>
    );
});